/**
 * Angebotsprüfung in der Detailmaske.
 *
 * Der Block steht zwischen Verlauf und letzter Aktivität und ist der einzige
 * Ort mit gestricheltem rotem Rahmen in der ganzen Anwendung. Er bedeutet:
 * unfertig und blockierend – ab „Gesendet" geht ohne ihn nichts weiter.
 *
 * Aufgeklappt zeigt er nur, was ein Urteil braucht. Erfüllte Kriterien liegen
 * hinter einem Textlink, abgehakte in einer eigenen Gruppe. Wer die Maske
 * öffnet, soll sehen, was zu tun ist – nicht, was schon erledigt war.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { IconSchloss } from './Icons';
import type { Angebot, OrdnerDatei, PruefAntwort, PruefErgebnis } from '../typen';
import './pruefblock.css';

/** Beispieltext im Begründungsfeld. Als Konstante, weil er Anführungszeichen enthält. */
const PLATZHALTER =
  'z. B. \u201eRAL 7016, wurde im Gespr\u00e4ch am 15.07. mit dem Kunden festgelegt.\u201c';

const STATUSWORT: Record<string, string> = {
  passt: 'passt',
  auffaellig: 'auffällig',
  nicht_ermittelbar: 'nicht ermittelbar',
};

/** Braucht dieses Kriterium noch ein Urteil? */
function offen(e: PruefErgebnis): boolean {
  return e.status !== 'passt' && !e.freigabeAm;
}

export function Pruefblock({ angebot }: { angebot: Angebot }) {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [aufgeklappt, setAufgeklappt] = useState(false);
  /** Für welche Rolle die Dateiauswahl gerade offen ist – oder null. */
  const [dateiwahl, setDateiwahl] = useState<'kunde' | 'lieferant' | null>(null);
  const [fehler, setFehler] = useState('');

  const abfrage = useQuery({
    queryKey: ['pruefung', angebot.id],
    queryFn: () => api.hole<PruefAntwort>(`/pruefung/angebot/${angebot.id}/pruefungen`),
  });

  const daten = abfrage.data;
  const juengste = daten?.pruefungen[0] ?? null;
  const stand = daten?.stand ?? angebot.pruefung;

  /** Lieferantenangebot wieder abwählen – die Prüfung läuft dann ohne. */
  const entfernen = useMutation({
    mutationFn: () =>
      api.aendere(`/pruefung/angebot/${angebot.id}/datei`, { rolle: 'lieferant', itemId: '' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pruefung', angebot.id] }),
  });

  const pruefen = useMutation({
    mutationFn: () => api.sende<{ toast: string }>(`/pruefung/angebot/${angebot.id}/pruefen`),
    onSuccess: (a) => {
      setFehler('');
      setAufgeklappt(true);
      void queryClient.invalidateQueries({ queryKey: ['pruefung', angebot.id] });
      void queryClient.invalidateQueries({ queryKey: ['angebot', angebot.id] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      toastZeigen(a.toast);
    },
    onError: (f) =>
      setFehler(f instanceof ApiFehler ? f.message : 'Die Prüfung ist fehlgeschlagen.'),
  });

  /* --- Zustand des Blocks: nie geprüft, offen, fertig, nicht eingerichtet --- */
  const nichtEingerichtet = !stand.moeglich;
  const fertig = !nichtEingerichtet && !stand.nieGeprueft && stand.abgeschlossen;

  const schlagzeile = nichtEingerichtet
    ? 'Prüfung noch nicht eingerichtet'
    : stand.nieGeprueft
      ? 'Noch nicht geprüft – Status ab „Gesendet" gesperrt'
      : fertig
        ? 'Prüfung abgeschlossen'
        : `${stand.offen} ${stand.offen === 1 ? 'Punkt braucht' : 'Punkte brauchen'} ein Urteil`;

  const ton = nichtEingerichtet ? 'grau' : fertig ? 'gruen' : 'offen';

  const zuBeurteilen = (juengste?.ergebnisse ?? []).filter(offen);
  const abgehakt = (juengste?.ergebnisse ?? []).filter((e) => !!e.freigabeAm);
  const erfuellt = (juengste?.ergebnisse ?? []).filter((e) => e.status === 'passt');

  return (
    <div className={`pb pb--${ton}`}>
      <div className="pb-kopf">
        <span className="pb-icon">
          <IconPruefung />
        </span>

        <div className="pb-text">
          <div className="pb-kicker">Angebotsprüfung</div>
          <div className="pb-zeile">
            <span className="pb-schlagzeile">{schlagzeile}</span>
            {/*
              Zeitpunkt und Person kommen aus der jüngsten Prüfung selbst, nicht
              aus dem Prüfstand: Der Stand sagt, wie viel offen ist, nicht wann
              geprüft wurde. Vorher stand hier nichts, weil ich die falsche
              Quelle abgefragt hatte.
            */}
            <span className="pb-meta">
              {juengste
                ? `Geprüft am ${new Date(juengste.zeitpunkt).toLocaleDateString('de-AT')} · ${new Date(
                    juengste.zeitpunkt,
                  ).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })} von ${juengste.nutzer}`
                : `Checkliste ${angebot.kategorie}`}
            </span>
          </div>
        </div>

        <div className="pb-knoepfe">
          {juengste && (
            <button
              type="button"
              className="pb-knopf"
              onClick={() => setAufgeklappt((a) => !a)}
            >
              Prüfung öffnen {aufgeklappt ? '▴' : '▾'}
            </button>
          )}
          {!nichtEingerichtet && (
            <button
              type="button"
              className={stand.nieGeprueft ? 'pb-knopf pb-knopf--prim' : 'pb-knopf'}
              disabled={pruefen.isPending}
              onClick={() => {
                setFehler('');
                if (!daten?.datei) setDateiwahl('kunde');
                else pruefen.mutate();
              }}
            >
              {pruefen.isPending
                ? 'Wird geprüft …'
                : stand.nieGeprueft
                  ? 'Angebot prüfen'
                  : 'Erneut prüfen'}
            </button>
          )}
        </div>
      </div>

      {/* --- Leerzustand: erklären, nicht leere Listen zeigen --- */}
      {nichtEingerichtet && (
        <p className="pb-erklaerung">
          {daten && !daten.kiAktiv
            ? 'Der Zugang zur Prüfung ist noch nicht hinterlegt. Bis dahin bleibt der Status frei wählbar.'
            : `Für ${angebot.kategorie} ist noch keine Checkliste hinterlegt. Die Geschäftsführung legt sie im Admin Center an. Bis dahin bleibt der Status frei wählbar.`}
        </p>
      )}

      {!nichtEingerichtet && stand.nieGeprueft && (
        <>
          <p className="pb-erklaerung">
            Kora liest das Angebot gegen die hinterlegte Checkliste und sagt zu jedem Punkt, ob er
            passt, auffällig ist oder sich nicht ermitteln lässt. Über die offenen Punkte
            entscheidest du.
          </p>
          <div className="pb-sperrzeile">
            <IconSchloss />
            <span>
              Solange nicht geprüft ist, lässt sich der Status nicht auf „Gesendet" oder weiter
              setzen.
            </span>
          </div>
        </>
      )}

      {/*
        Zwei Dateien, zwei Rollen. Das Kundenangebot ist Pflicht, das
        Lieferantenangebot nicht – aber sein Fehlen wird ein Prüfpunkt, kein
        stillschweigendes Übergehen. Deshalb steht es hier auch dann, wenn
        keines gewählt ist.
      */}
      {!nichtEingerichtet && (
        <div className="pb-dateien">
          <div className="pb-datei">
            <span className="pb-datei-rolle">Kundenangebot</span>
            {daten?.datei ? (
              <strong className="pb-datei-name">{daten.datei.name}</strong>
            ) : (
              <span className="pb-datei-leer">noch nicht gewählt</span>
            )}
            <button type="button" className="pb-textlink" onClick={() => setDateiwahl('kunde')}>
              {daten?.datei ? 'andere wählen' : 'wählen'}
            </button>
          </div>

          <div className="pb-datei">
            <span className="pb-datei-rolle">Lieferantenangebot</span>
            {daten?.lieferantdatei ? (
              <strong className="pb-datei-name">{daten.lieferantdatei.name}</strong>
            ) : (
              <span className="pb-datei-leer">keines – wird zum offenen Prüfpunkt</span>
            )}
            <button type="button" className="pb-textlink" onClick={() => setDateiwahl('lieferant')}>
              {daten?.lieferantdatei ? 'andere wählen' : 'wählen'}
            </button>
            {daten?.lieferantdatei && (
              <button
                type="button"
                className="pb-textlink"
                onClick={() => entfernen.mutate()}
                disabled={entfernen.isPending}
              >
                entfernen
              </button>
            )}
          </div>
        </div>
      )}

      {fehler && <div className="eh-fehler">{fehler}</div>}

      {aufgeklappt && juengste && (
        <div className="pb-liste">
          {zuBeurteilen.map((e) => (
            <Kriterium key={e.id} ergebnis={e} angebotId={angebot.id} />
          ))}

          {zuBeurteilen.length === 0 && (
            <div className="pb-fertigzeile">Alle Punkte sind erfüllt oder abgehakt.</div>
          )}

          {abgehakt.length > 0 && (
            <Gruppe titel={`${abgehakt.length} Punkte abgehakt`} ton="gruen">
              {abgehakt.map((e) => (
                <Kriterium key={e.id} ergebnis={e} angebotId={angebot.id} />
              ))}
            </Gruppe>
          )}

          {erfuellt.length > 0 && (
            <Erfuellte ergebnisse={erfuellt} />
          )}
        </div>
      )}

      {dateiwahl && (
        <Dateiwahl
          angebotId={angebot.id}
          rolle={dateiwahl}
          onSchliessen={() => setDateiwahl(null)}
          onGewaehlt={() => {
            setDateiwahl(null);
            void queryClient.invalidateQueries({ queryKey: ['pruefung', angebot.id] });
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Ein Kriterium mit Urteil.
 *
 * Zugeklappt eine Zeile, aufgeklappt Begründung, Fundstelle und der Weg zum
 * Abhaken. Es ist immer nur eine Zeile offen – wer zwei Begründungen
 * nebeneinander liest, vergleicht sie, statt zu entscheiden.
 */
function Kriterium({ ergebnis, angebotId }: { ergebnis: PruefErgebnis; angebotId: number }) {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [offenerText, setOffenerText] = useState(false);
  const [bemerkung, setBemerkung] = useState('');
  const [fehler, setFehler] = useState('');

  const freigegeben = !!ergebnis.freigabeAm;

  const abhaken = useMutation({
    mutationFn: () =>
      api.sende<{ toast: string }>(`/pruefung/ergebnis/${ergebnis.id}/freigabe`, {
        bemerkung: bemerkung.trim(),
      }),
    onSuccess: (a) => {
      setBemerkung('');
      setOffenerText(false);
      void queryClient.invalidateQueries({ queryKey: ['pruefung', angebotId] });
      void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      toastZeigen(a.toast);
    },
    onError: (f) => setFehler(f instanceof ApiFehler ? f.message : 'Das Abhaken ist fehlgeschlagen.'),
  });

  const zuruecknehmen = useMutation({
    mutationFn: () => api.loesche<{ toast: string }>(`/pruefung/ergebnis/${ergebnis.id}/freigabe`),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['pruefung', angebotId] });
      void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      toastZeigen(a.toast);
    },
  });

  return (
    <div className={`pb-kriterium pb-kriterium--${freigegeben ? 'frei' : ergebnis.status}`}>
      <button
        type="button"
        className="pb-kriterium-zeile"
        onClick={() => setOffenerText((o) => !o)}
      >
        <span className={`pb-punkt pb-punkt--${freigegeben ? 'frei' : ergebnis.status}`}>
          {freigegeben || ergebnis.status === 'passt' ? '✓' : ergebnis.status === 'auffaellig' ? '!' : '?'}
        </span>
        <span className="pb-kriterium-name">{ergebnis.kriteriumText}</span>
        <span className={`pb-statuswort pb-statuswort--${freigegeben ? 'frei' : ergebnis.status}`}>
          {freigegeben
            ? `freigegeben · war ${STATUSWORT[ergebnis.status]}`
            : STATUSWORT[ergebnis.status]}
        </span>
        <span className="pb-pfeil">{offenerText ? '▴' : '▾'}</span>
      </button>

      {offenerText && (
        <div className="pb-kriterium-inhalt">
          <p className="pb-begruendung">{ergebnis.begruendung}</p>
          {ergebnis.fundstelle && (
            <p className="pb-fundstelle">Fundstelle: {ergebnis.fundstelle}</p>
          )}

          {freigegeben ? (
            <div className="pb-freigabe">
              <p className="pb-freigabe-text">„{ergebnis.freigabeBemerkung}"</p>
              <div className="pb-freigabe-fuss">
                Abgehakt von {ergebnis.freigabeVon} ·{' '}
                {ergebnis.freigabeAm ? new Date(ergebnis.freigabeAm).toLocaleString('de-AT') : ''}
                <button
                  type="button"
                  className="pb-textlink"
                  onClick={() => zuruecknehmen.mutate()}
                >
                  Haken zurücknehmen
                </button>
              </div>
            </div>
          ) : (
            <div className="pb-abhaken">
              <label className="pb-feld">
                <span>Warum ist das in Ordnung? · Pflichtfeld</span>
                <textarea
                  className="modal-textarea"
                  rows={3}
                  value={bemerkung}
                  placeholder={PLATZHALTER}
                  onChange={(e) => {
                    setBemerkung(e.target.value);
                    setFehler('');
                  }}
                />
              </label>
              <button
                type="button"
                className="pb-abhakknopf"
                disabled={!bemerkung.trim() || abhaken.isPending}
                onClick={() => abhaken.mutate()}
              >
                {abhaken.isPending ? 'Wird gespeichert …' : 'Abhaken'}
              </button>
              {fehler && <div className="eh-fehler">{fehler}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Aufklappbare Gruppe – die abgehakten Punkte liegen darin. */
function Gruppe({
  titel,
  ton,
  children,
}: {
  titel: string;
  ton: string;
  children: React.ReactNode;
}) {
  const [offenGruppe, setOffenGruppe] = useState(false);
  return (
    <div className={`pb-gruppe pb-gruppe--${ton}`}>
      <button type="button" className="pb-gruppe-kopf" onClick={() => setOffenGruppe((o) => !o)}>
        <span>{offenGruppe ? '▾' : '▸'}</span>
        {titel}
      </button>
      {offenGruppe && <div className="pb-gruppe-inhalt">{children}</div>}
    </div>
  );
}

/** Erfüllte Kriterien: einzeilig, ohne Rahmen, hinter einem Textlink. */
function Erfuellte({ ergebnisse }: { ergebnisse: PruefErgebnis[] }) {
  const [sichtbar, setSichtbar] = useState(false);
  return (
    <div className="pb-erfuellt">
      <button type="button" className="pb-textlink" onClick={() => setSichtbar((s) => !s)}>
        {sichtbar ? 'Erfüllte Kriterien ausblenden' : `${ergebnisse.length} erfüllte Kriterien anzeigen`}
      </button>
      {sichtbar && (
        <ul className="pb-erfuellt-liste">
          {ergebnisse.map((e) => (
            <li key={e.id}>
              <span className="pb-punkt pb-punkt--passt">✓</span>
              {e.kriteriumText}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Auswahl der zu prüfenden Datei.
 *
 * Kora rät nie. Sie legt vor, was im Vorgangsordner liegt, und ein Mensch
 * entscheidet – auch dann, wenn nur eine Datei da ist.
 */
function Dateiwahl({
  angebotId,
  rolle,
  onSchliessen,
  onGewaehlt,
}: {
  angebotId: number;
  rolle: 'kunde' | 'lieferant';
  onSchliessen: () => void;
  onGewaehlt: () => void;
}) {
  const abfrage = useQuery({
    queryKey: ['pruefdateien', angebotId],
    queryFn: () =>
      api.hole<{ dateien: OrdnerDatei[]; hinweis: string }>(
        `/pruefung/angebot/${angebotId}/dateien`,
      ),
  });

  const waehlen = useMutation({
    mutationFn: (d: OrdnerDatei) =>
      api.aendere(`/pruefung/angebot/${angebotId}/datei`, {
        rolle,
        itemId: d.itemId,
        name: d.name,
        stand: d.stand,
      }),
    onSuccess: onGewaehlt,
  });

  return (
    <div className="pb-wahl">
      <div className="pb-wahl-kopf">
        <span>
          {rolle === 'kunde'
            ? 'Welches Angebot an den Kunden soll geprüft werden?'
            : 'Welches Lieferantenangebot gehört dazu?'}
        </span>
        <button type="button" className="pb-textlink" onClick={onSchliessen}>
          schließen
        </button>
      </div>

      {abfrage.isLoading && <div className="pb-wahl-leer">Ordner wird gelesen …</div>}
      {abfrage.data?.hinweis && <div className="pb-wahl-leer">{abfrage.data.hinweis}</div>}

      {(abfrage.data?.dateien ?? []).map((d) => (
        <button
          key={d.itemId}
          type="button"
          className="pb-wahl-datei"
          disabled={waehlen.isPending}
          onClick={() => waehlen.mutate(d)}
        >
          <span className="pb-wahl-name">{d.name}</span>
          <span className="pb-wahl-stand">
            {d.stand ? new Date(d.stand).toLocaleDateString('de-AT') : ''}
          </span>
        </button>
      ))}
    </div>
  );
}

/** Prüfsymbol – ein Blatt mit Haken. */
function IconPruefung({ groesse = 16 }: { groesse?: number }) {
  return (
    <svg
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5" />
      <path d="M9.5 14.5l1.8 1.8 3.4-3.6" />
    </svg>
  );
}